/**
 * Simple logging utility with configurable log levels
 *
 * Log Levels (from least to most verbose):
 * - NONE: No logging
 * - ERROR: Only errors
 * - WARN: Errors and warnings
 * - INFO: Errors, warnings, and info
 * - DEBUG: Everything (most verbose)
 *
 * Set REACT_APP_LOG_LEVEL in .env file
 */

const LOG_LEVELS = {
  NONE: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4,
};

// Get log level from environment, default to INFO in development, ERROR in production
const getLogLevel = () => {
  const envLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();

  if (envLevel && LOG_LEVELS[envLevel] !== undefined) {
    return LOG_LEVELS[envLevel];
  }

  // Default: INFO in development, ERROR in production
  return process.env.NODE_ENV === 'production' ? LOG_LEVELS.ERROR : LOG_LEVELS.INFO;
};

const currentLogLevel = getLogLevel();

const logger = {
  /**
   * Log debug information (most verbose)
   * Use for detailed debugging during development
   */
  debug: (...args) => {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.debug('🔍', ...args);
    }
  },

  /**
   * Log informational messages
   * Use for general app flow and user actions
   */
  info: (...args) => {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      console.info('ℹ️', ...args);
    }
  },

  /**
   * Log warnings
   * Use for deprecated features or potential issues
   */
  warn: (...args) => {
    if (currentLogLevel >= LOG_LEVELS.WARN) {
      console.warn('⚠️', ...args);
    }
  },

  /**
   * Log errors
   * Always logged unless level is NONE
   */
  error: (...args) => {
    if (currentLogLevel >= LOG_LEVELS.ERROR) {
      console.error('❌', ...args);
    }
  },

  /**
   * Log with custom emoji/prefix
   * Respects INFO level
   */
  log: (emoji, ...args) => {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      console.log(emoji, ...args);
    }
  },

  /**
   * Group related logs together
   */
  group: (label) => {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.group(label);
    }
  },

  groupEnd: () => {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.groupEnd();
    }
  },

  /**
   * Display data in table format
   */
  table: (data) => {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.table(data);
    }
  },

  /**
   * Get current log level name
   */
  getLevel: () => {
    return Object.keys(LOG_LEVELS).find((key) => LOG_LEVELS[key] === currentLogLevel);
  },
};

// Log the current log level on startup
if (currentLogLevel >= LOG_LEVELS.INFO) {
  console.log(`📊 Log level: ${logger.getLevel()}`);
}

export default logger;
